import { useEffect, useRef } from 'react'
import { useAuth } from '../hooks/context/auth'

declare global {
  interface Window {
    adsbygoogle: Array<Record<string, unknown>>
  }
}

interface AdBannerProps {
  adSlot: string
  adFormat?: 'auto' | 'rectangle' | 'horizontal' | 'vertical'
  className?: string
  style?: React.CSSProperties
}

/**
 * Google AdSense banner component.
 * Automatically hidden for premium users.
 *
 * Requires VITE_ADSENSE_CLIENT_ID env var (e.g. "ca-pub-XXXXXXXXXX").
 * Ad slot IDs come from the AdSense dashboard.
 */
export const AdBanner = ({ adSlot, adFormat = 'auto', className = '', style }: AdBannerProps) => {
  const { user } = useAuth()
  const adRef = useRef<HTMLModElement>(null)
  const clientId = import.meta.env.VITE_ADSENSE_CLIENT_ID

  useEffect(() => {
    if (user?.isPremium || !clientId) return
    try {
      ;(window.adsbygoogle = window.adsbygoogle || []).push({})
    } catch {
      // AdSense not loaded or blocked
    }
  }, [user?.isPremium, clientId])

  // Don't render ads for premium users or if no client ID configured
  if (user?.isPremium || !clientId) return null

  return (
    <div className={`ad-banner ${className}`} style={style}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block', ...(style || {}) }}
        data-ad-client={clientId}
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-full-width-responsive="true"
      />
    </div>
  )
}

export default AdBanner
